import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  getPublicFormBySlugApi,
  submitPublicFormResponseApi,
} from "../api/TaskActions";

type FormField = {
  field_id: number;
  task_id: number;
  field_label: string;
  field_type: string;
  field_order: number;
  is_required: boolean | number;
  options?: string[];
  options_json?: string | null;
};

type PublicForm = {
  task_id: number;
  task_title: string;
  task_description?: string | null;
  deadline?: string | null;
  allowed_responses?: number;
  audience_type: "public";
  public_form_slug: string;
  status: string;
  created_at?: string;
  created_by?: number;
  client_name?: string | null;
  form_fields: FormField[];
};

type AnswerValue =
  | string
  | number
  | string[]
  | File
  | null;

type AnswersState = Record<number, AnswerValue>;

type LoggedInUser = {
  user_id?: number;
  phone_number?: string;
  user_fullname?: string;
  email?: string;
  dob?: string;
  profile_photo?: string;
};

const parseOptions = (field: FormField): string[] => {
  if (Array.isArray(field.options)) {
    return field.options;
  }

  if (!field.options_json) {
    return [];
  }

  try {
    const parsed = JSON.parse(field.options_json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getDeadlineLabel = (value?: string | null) => {
  if (!value) return "No deadline";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isAnswered = (value: AnswerValue) => {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
};

export default function PublicFormPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const loggedUser = useMemo<LoggedInUser>(() => {
    try {
      return JSON.parse(
        localStorage.getItem("user") || "{}",
      ) as LoggedInUser;
    } catch {
      return {};
    }
  }, []);

  const loggedUserId = Number(
    loggedUser?.user_id || 0,
  );

  const isLoggedIn =
    Number.isInteger(loggedUserId) &&
    loggedUserId > 0;

  const [form, setForm] = useState<PublicForm | null>(null);
  const [answers, setAnswers] = useState<AnswersState>({});

  const [respondentName, setRespondentName] = useState("");
  const [respondentEmail, setRespondentEmail] = useState("");
  const [respondentPhone, setRespondentPhone] = useState("");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [focusedField, setFocusedField] = useState<number | null>(null);

  const orderedFields = useMemo(
    () =>
      [...(form?.form_fields || [])].sort(
        (a, b) =>
          Number(a.field_order || 0) -
          Number(b.field_order || 0),
      ),
    [form],
  );

  const requiredFields = useMemo(
    () => orderedFields.filter((field) => Boolean(field.is_required)),
    [orderedFields],
  );

  const answeredRequiredCount = useMemo(
    () =>
      requiredFields.filter((field) =>
        isAnswered(answers[field.field_id]),
      ).length,
    [requiredFields, answers],
  );

  const progressPercent = requiredFields.length
    ? Math.round((answeredRequiredCount / requiredFields.length) * 100)
    : 100;

  const fetchPublicForm = useCallback(async () => {
    if (!slug) {
      setErrorMessage("Invalid public form link.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");

      const data =
        await getPublicFormBySlugApi(slug);

      if (
        data?.success === false ||
        data?.status === false
      ) {
        throw new Error(
          data?.message ||
            "Unable to load this public form."
        );
      }

      setForm(
        data?.data || data?.form || null
      );
    } catch (error) {
      console.error("Public form fetch error:", error);

      setForm(null);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Unable to load this public form.",
      );
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPublicForm();
  }, [fetchPublicForm]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    setRespondentName(
      loggedUser.user_fullname || "",
    );

    setRespondentEmail(
      loggedUser.email || "",
    );

    setRespondentPhone(
      loggedUser.phone_number || "",
    );
  }, [
    isLoggedIn,
    loggedUser.email,
    loggedUser.phone_number,
    loggedUser.user_fullname,
  ]);

  const updateAnswer = (
    fieldId: number,
    value: AnswerValue,
  ) => {
    setAnswers((current) => ({
      ...current,
      [fieldId]: value,
    }));

    setErrorMessage("");
  };

  const toggleMultiValue = (
    fieldId: number,
    option: string,
  ) => {
    const currentValue = answers[fieldId];
    const selectedValues = Array.isArray(currentValue)
      ? currentValue
      : [];

    const nextValues = selectedValues.includes(option)
      ? selectedValues.filter((item) => item !== option)
      : [...selectedValues, option];

    updateAnswer(fieldId, nextValues);
  };

  const validateForm = () => {
    if (!form) {
      return "Public form is unavailable.";
    }

    if (!respondentName.trim()) {
      return "Full Name is required.";
    }

    if (!respondentEmail.trim()) {
      return "Email Address is required.";
    }

    if (!respondentPhone.trim()) {
      return "Contact Number is required.";
    }

    for (const field of orderedFields) {
      if (!Boolean(field.is_required)) continue;

      const value = answers[field.field_id];

      if (!isAnswered(value)) {
        return `${field.field_label} is required.`;
      }
    }

    return null;
  };

  const buildAnswerPayload = (field: FormField) => {
    const value = answers[field.field_id];

    const base = {
      field_id: field.field_id,
    };

    switch (field.field_type) {
      case "number":
      case "rating":
        return {
          ...base,
          answer_number:
            value === "" || value === null || value === undefined
              ? null
              : Number(value),
        };

      case "date":
        return {
          ...base,
          answer_date: value || null,
        };

      case "select":
      case "multiselect":
      case "yesno":
        return {
          ...base,
          selected_values: Array.isArray(value)
            ? value
            : value
              ? [String(value)]
              : [],
        };

      case "file":
      case "image":
        return {
          ...base,
          answer_file:
            typeof value === "string" ? value : null,
        };

      default:
        return {
          ...base,
          answer_text:
            value === null || value === undefined
              ? null
              : String(value),
        };
    }
  };

  const submitForm = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    if (!slug || !form) {
      setErrorMessage("Public form is unavailable.");
      return;
    }

    const validationError = validateForm();

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setErrorMessage("");
      setSuccessMessage("");

      const formData = new FormData();

      if (isLoggedIn) {
        formData.append(
          "user_id",
          String(loggedUserId),
        );
      }

      formData.append(
        "respondent_name",
        respondentName.trim(),
      );

      formData.append(
        "respondent_email",
        respondentEmail.trim(),
      );

      formData.append(
        "respondent_phone",
        respondentPhone.trim(),
      );

      const answerPayload = orderedFields.map((field) => {
        const value = answers[field.field_id];

        if (
          (field.field_type === "file" ||
            field.field_type === "image") &&
          value instanceof File
        ) {
          formData.append(
            `file_${field.field_id}`,
            value,
          );

          return {
            field_id: field.field_id,
          };
        }

        return buildAnswerPayload(field);
      });

      formData.append(
        "answers",
        JSON.stringify(answerPayload),
      );

      const data =
        await submitPublicFormResponseApi(
          slug,
          formData
        );

      if (
        data?.success === false ||
        data?.status === false
      ) {
        throw new Error(
          data?.message ||
            "Unable to submit the form."
        );
      }

      setSuccessMessage(
        data.message ||
          "Your response was submitted successfully.",
      );

      setAnswers({});

      if (isLoggedIn) {
        setRespondentName(
          loggedUser.user_fullname || "",
        );

        setRespondentEmail(
          loggedUser.email || "",
        );

        setRespondentPhone(
          loggedUser.phone_number || "",
        );
      } else {
        setRespondentName("");
        setRespondentEmail("");
        setRespondentPhone("");
      }

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (error) {
      console.error(
        "Public form submission error:",
        error,
      );

      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Unable to submit the form.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const value = answers[field.field_id];
    const options = parseOptions(field);

    switch (field.field_type) {
      case "textarea":
        return (
          <textarea
            className="bsf-input bsf-textarea"
            value={String(value || "")}
            onChange={(event) =>
              updateAnswer(field.field_id, event.target.value)
            }
            onFocus={() => setFocusedField(field.field_id)}
            onBlur={() => setFocusedField(null)}
            rows={5}
            placeholder={`Enter ${field.field_label.toLowerCase()}`}
          />
        );

      case "number":
        return (
          <input
            className="bsf-input"
            type="number"
            value={
              typeof value === "number" || typeof value === "string"
                ? value
                : ""
            }
            onChange={(event) =>
              updateAnswer(field.field_id, event.target.value)
            }
            onFocus={() => setFocusedField(field.field_id)}
            onBlur={() => setFocusedField(null)}
          />
        );

      case "date":
        return (
          <input
            className="bsf-input"
            type="date"
            value={String(value || "")}
            onChange={(event) =>
              updateAnswer(field.field_id, event.target.value)
            }
            onFocus={() => setFocusedField(field.field_id)}
            onBlur={() => setFocusedField(null)}
          />
        );

      case "email":
        return (
          <input
            className="bsf-input"
            type="email"
            value={String(value || "")}
            onChange={(event) =>
              updateAnswer(field.field_id, event.target.value)
            }
            onFocus={() => setFocusedField(field.field_id)}
            onBlur={() => setFocusedField(null)}
            placeholder="example@email.com"
          />
        );

      case "url":
        return (
          <input
            className="bsf-input"
            type="url"
            value={String(value || "")}
            onChange={(event) =>
              updateAnswer(field.field_id, event.target.value)
            }
            onFocus={() => setFocusedField(field.field_id)}
            onBlur={() => setFocusedField(null)}
            placeholder="https://example.com"
          />
        );

      case "phone":
        return (
          <input
            className="bsf-input"
            type="tel"
            value={String(value || "")}
            onChange={(event) =>
              updateAnswer(field.field_id, event.target.value)
            }
            onFocus={() => setFocusedField(field.field_id)}
            onBlur={() => setFocusedField(null)}
            placeholder="Enter contact number"
          />
        );

      case "select":
        return (
          <select
            className="bsf-input bsf-select"
            value={String(value || "")}
            onChange={(event) =>
              updateAnswer(field.field_id, event.target.value)
            }
            onFocus={() => setFocusedField(field.field_id)}
            onBlur={() => setFocusedField(null)}
          >
            <option value="">Select an option</option>

            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "multiselect":
        return (
          <div className="bsf-option-list">
            {options.map((option) => {
              const selected = Array.isArray(value)
                ? value.includes(option)
                : false;

              return (
                <label
                  key={option}
                  className={`bsf-option-item${
                    selected ? " bsf-option-item--active" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() =>
                      toggleMultiValue(field.field_id, option)
                    }
                  />
                  <span>{option}</span>
                </label>
              );
            })}
          </div>
        );

      case "yesno":
        return (
          <div className="bsf-option-list">
            {["Yes", "No"].map((option) => (
              <label
                key={option}
                className={`bsf-option-item${
                  value === option ? " bsf-option-item--active" : ""
                }`}
              >
                <input
                  type="radio"
                  name={`field-${field.field_id}`}
                  value={option}
                  checked={value === option}
                  onChange={() => updateAnswer(field.field_id, option)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case "rating":
        return (
          <div className="bsf-rating-wrap">
            {[1, 2, 3, 4, 5].map((rating) => (
              <button
                key={rating}
                type="button"
                onClick={() => updateAnswer(field.field_id, rating)}
                className={`bsf-rating-btn${
                  value === rating ? " bsf-rating-btn--active" : ""
                }`}
              >
                {rating}
              </button>
            ))}
          </div>
        );

      case "file":
      case "image":
        return (
          <div>
            <label className="bsf-file-drop">
              <input
                type="file"
                accept={field.field_type === "image" ? "image/*" : undefined}
                onChange={(event) =>
                  updateAnswer(
                    field.field_id,
                    event.target.files?.[0] || null,
                  )
                }
              />
              <span className="bsf-file-drop-icon">
                {field.field_type === "image" ? "🖼" : "📎"}
              </span>
              <span className="bsf-file-drop-text">
                {value instanceof File
                  ? value.name
                  : `Click to choose a ${
                      field.field_type === "image" ? "photo" : "file"
                    }`}
              </span>
            </label>

            <p className="bsf-helper-text">
              The selected file will be uploaded with your response.
            </p>
          </div>
        );

      default:
        return (
          <input
            className="bsf-input"
            type="text"
            value={String(value || "")}
            onChange={(event) =>
              updateAnswer(field.field_id, event.target.value)
            }
            onFocus={() => setFocusedField(field.field_id)}
            onBlur={() => setFocusedField(null)}
            placeholder={`Enter ${field.field_label.toLowerCase()}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="bsf-page">
        <BsfStyles />
        <div className="bsf-shell">
          <div className="bsf-skel bsf-skel-hero">
            <div className="bsf-skel-row">
              <div className="bsf-skel-block" style={{ width: 96, height: 28 }} />
              <div className="bsf-skel-block" style={{ width: 120, height: 16 }} />
            </div>
            <div className="bsf-skel-block" style={{ width: "70%", height: 30, marginTop: 18 }} />
            <div className="bsf-skel-block" style={{ width: "45%", height: 14, marginTop: 12 }} />
          </div>

          <div className="bsf-skel bsf-skel-card">
            <div className="bsf-skel-block" style={{ width: "30%", height: 18, marginBottom: 16 }} />
            {[0, 1, 2].map((i) => (
              <div className="bsf-skel-block" key={i} style={{ width: "100%", height: 48, marginBottom: 14, borderRadius: 12 }} />
            ))}
          </div>

          <div className="bsf-skel bsf-skel-card">
            <div className="bsf-skel-block" style={{ width: "24%", height: 18, marginBottom: 16 }} />
            {[0, 1, 2, 3].map((i) => (
              <div className="bsf-skel-block" key={i} style={{ width: "100%", height: 70, marginBottom: 14, borderRadius: 14 }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="bsf-page">
        <BsfStyles />
        <div className="bsf-state-card">
          <div className="bsf-state-icon">◇</div>
          <h2 className="bsf-state-title">Form unavailable</h2>
          <p className="bsf-state-text">
            {errorMessage ||
              "This public form does not exist or is no longer available."}
          </p>
          <button
            type="button"
            className="bsf-btn bsf-btn-secondary"
            onClick={() => navigate("/")}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bsf-page">
      <BsfStyles />
      <div className="bsf-shell">
        <header className="bsf-hero">
          <div className="bsf-hero-glow" aria-hidden="true" />

          <div className="bsf-hero-top">
            <img src="/assets/logo.png" alt="Bharat Sangam" className="bsf-logo" />

            {form.client_name && (
              <span className="bsf-client-chip">{form.client_name}</span>
            )}
          </div>

          <h1 className="bsf-hero-title">{form.task_title}</h1>

          {form.task_description && (
            <p className="bsf-hero-subtitle">{form.task_description}</p>
          )}

          <div className="bsf-hero-meta">
            <span className="bsf-deadline-chip">
              <span className="bsf-deadline-dot" />
              {getDeadlineLabel(form.deadline)}
            </span>

            {requiredFields.length > 0 && (
              <span className="bsf-progress-chip">
                {answeredRequiredCount} / {requiredFields.length} required answered
              </span>
            )}
          </div>

          {requiredFields.length > 0 && (
            <div className="bsf-progress-track">
              <div
                className="bsf-progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          )}
        </header>

        {successMessage && (
          <div className="bsf-alert bsf-alert-success">
            <span className="bsf-alert-icon">✓</span>
            <div>
              <strong>Submitted successfully</strong>
              <p>{successMessage}</p>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="bsf-alert bsf-alert-error">
            <span className="bsf-alert-icon">!</span>
            <p>{errorMessage}</p>
          </div>
        )}

        <form onSubmit={submitForm} className="bsf-form-card">
          <section className="bsf-respondent-section">
            <h2 className="bsf-section-title">Applicant Details</h2>
            <p className="bsf-section-description">
              {isLoggedIn
                ? "Your logged-in account details are filled automatically."
                : "Enter your basic contact information."}
            </p>

            {isLoggedIn && (
              <div className="bsf-account-notice">
                <span className="bsf-account-notice-icon">
                  ✓
                </span>

                <div>
                  <strong>
                    Filling as {loggedUser.user_fullname}
                  </strong>

                  <p>
                    This response will be linked to your Bharat Sangam account.
                  </p>
                </div>
              </div>
            )}

            <div className="bsf-grid">
              <div>
                <label className="bsf-label">Full Name</label>
                <input
                  className="bsf-input"
                  type="text"
                  value={respondentName}
                  onChange={(event) =>
                    setRespondentName(
                      event.target.value,
                    )
                  }
                  placeholder="Enter your full name"
                  readOnly={isLoggedIn}
                />
              </div>

              <div>
                <label className="bsf-label">Email Address</label>
                <input
                  className="bsf-input"
                  type="email"
                  value={respondentEmail}
                  onChange={(event) =>
                    setRespondentEmail(
                      event.target.value,
                    )
                  }
                  placeholder="example@email.com"
                  readOnly={isLoggedIn}
                />
              </div>

              <div>
                <label className="bsf-label">Contact Number</label>
                <input
                  className="bsf-input"
                  type="tel"
                  value={respondentPhone}
                  onChange={(event) =>
                    setRespondentPhone(
                      event.target.value,
                    )
                  }
                  placeholder="Enter contact number"
                  readOnly={isLoggedIn}
                />
              </div>
            </div>
          </section>

          <section>
            <h2 className="bsf-section-title">Form Questions</h2>
            <p className="bsf-section-description">
              Fields marked <span className="bsf-required">*</span> are required.
            </p>

            <div className="bsf-fields-list">
              {orderedFields.map((field, index) => {
                const answered = isAnswered(answers[field.field_id]);
                const active = focusedField === field.field_id;

                return (
                  <div
                    key={field.field_id}
                    className={`bsf-field-card${active ? " bsf-field-card--active" : ""}`}
                  >
                    <label className="bsf-label bsf-field-label">
                      <span className="bsf-field-number">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="bsf-field-label-text">
                        {field.field_label}
                        {Boolean(field.is_required) && (
                          <span className="bsf-required">*</span>
                        )}
                      </span>
                      {Boolean(field.is_required) && answered && (
                        <span className="bsf-field-check">✓</span>
                      )}
                    </label>

                    <div className="bsf-field-control">{renderField(field)}</div>
                  </div>
                );
              })}
            </div>
          </section>

          <div className="bsf-action-row">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="bsf-btn bsf-btn-secondary"
              disabled={submitting}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="bsf-btn bsf-btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="bsf-spinner" /> Submitting…
                </>
              ) : (
                "Submit Response"
              )}
            </button>
          </div>
        </form>

        <footer className="bsf-footer">Powered by Bharat Sangam</footer>
      </div>
    </div>
  );
}

function BsfStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=swap');

      .bsf-page {
        min-height: 100vh;
        background: #F4F7FD;
        background-image:
          radial-gradient(circle at 12% 0%, rgba(14,165,233,0.08), transparent 40%),
          radial-gradient(circle at 88% 8%, rgba(10,35,84,0.06), transparent 35%);
        padding: 40px 16px 64px;
        color: #0A2354;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      }

      .bsf-shell { width: 100%; max-width: 800px; margin: 0 auto; }

      /* ---------- Hero ---------- */
      .bsf-hero {
        position: relative;
        overflow: hidden;
        background: #0EA5E9;
        color: #ffffff;
        border-radius: 22px;
        padding: 26px 28px 24px;
        box-shadow: 0 16px 40px rgba(10, 35, 84, 0.24);
      }

      .bsf-hero-glow {
        position: absolute;
        top: -60px;
        right: -60px;
        width: 220px;
        height: 220px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(255,255,255,0.16), transparent 70%);
        pointer-events: none;
      }

      .bsf-hero-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 14px;
        margin-bottom: 16px;
        position: relative;
      }
      

.bsf-logo {
  width: 108px;
  max-width: 100%;
  height: auto;
  object-fit: contain;
  opacity: 0.96;
}
      .bsf-client-chip {
        color: #ffffff;
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
        background: rgba(255,255,255,0.14);
        border: 1px solid rgba(255,255,255,0.22);
        padding: 5px 12px;
        border-radius: 999px;
      }

      .bsf-hero-title {
        margin: 0;
        font-family: 'Fraunces', Georgia, serif;
        font-optical-sizing: auto;
        font-size: clamp(24px, 4vw, 32px);
        line-height: 1.18;
        font-weight: 600;
        letter-spacing: -0.3px;
        position: relative;
      }

      .bsf-hero-subtitle {
        margin: 9px 0 0;
        max-width: 640px;
        color: rgba(255,255,255,0.82);
        font-size: 14.5px;
        line-height: 1.6;
        position: relative;
      }

      .bsf-hero-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 18px;
        position: relative;
      }

      .bsf-deadline-chip {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        font-size: 12px;
        font-weight: 600;
        color: rgba(255,255,255,0.92);
        background: rgba(255,255,255,0.1);
        border: 1px solid rgba(255,255,255,0.18);
        padding: 6px 12px;
        border-radius: 999px;
      }

      .bsf-deadline-dot {
        width: 6px; height: 6px; border-radius: 50%;
        background: #7FB3FF;
        box-shadow: 0 0 0 3px rgba(127,179,255,0.25);
      }

      .bsf-progress-chip {
        display: inline-flex;
        align-items: center;
        font-size: 12px;
        font-weight: 600;
        color: #0A2354;
        background: #ffffff;
        padding: 6px 12px;
        border-radius: 999px;
      }

      .bsf-progress-track {
        margin-top: 14px;
        height: 5px;
        border-radius: 999px;
        background: rgba(255,255,255,0.16);
        overflow: hidden;
        position: relative;
      }

      .bsf-progress-fill {
        height: 100%;
        border-radius: 999px;
        background: #ffffff;
        transition: width 0.35s ease;
      }

      /* ---------- Alerts ---------- */
      .bsf-alert {
        margin-top: 18px;
        display: flex;
        align-items: flex-start;
        gap: 12px;
        border-radius: 16px;
        padding: 14px 16px;
        font-size: 13.5px;
        line-height: 1.55;
      }

      .bsf-alert-success {
        border: 1px solid #C7DBFB;
        background: #EEF4FE;
        color: #0A2354;
      }
      .bsf-alert-success strong { display: block; margin-bottom: 2px; font-weight: 700; }
      .bsf-alert-success p { margin: 0; color: #3D5480; }

      .bsf-alert-error {
        border: 1px solid #F1C6C6;
        background: #FDF1F1;
        color: #8C2C2C;
        font-weight: 500;
      }
      .bsf-alert-error p { margin: 0; }

      .bsf-alert-icon {
        flex-shrink: 0;
        width: 22px; height: 22px;
        border-radius: 50%;
        display: inline-flex; align-items: center; justify-content: center;
        font-size: 12px; font-weight: 800;
      }
      .bsf-alert-success .bsf-alert-icon { background: #0EA5E9; color: #fff; }
      .bsf-alert-error .bsf-alert-icon { background: #C24545; color: #fff; }

      /* ---------- Form card ---------- */
      .bsf-form-card {
        margin-top: 22px;
        background: #ffffff;
        border: 1px solid #E3EBF9;
        border-radius: 22px;
        padding: 26px;
        box-shadow: 0 10px 30px rgba(10, 35, 84, 0.05);
      }

      .bsf-respondent-section {
        border-bottom: 1px solid #EAF1FE;
        padding-bottom: 26px;
        margin-bottom: 26px;
      }

      .bsf-section-title {
        margin: 0;
        font-family: 'Fraunces', Georgia, serif;
        font-size: 20px;
        font-weight: 600;
        color: #0A2354;
      }

      .bsf-section-description {
        margin: 6px 0 18px;
        font-size: 13.5px;
        color: #6B7A9B;
        line-height: 1.6;
      }

      .bsf-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
        gap: 16px;
      }

      .bsf-label {
        display: block;
        margin-bottom: 8px;
        font-size: 13px;
        font-weight: 700;
        color: #2C3E63;
      }

      .bsf-required { color: #0EA5E9; margin-left: 3px; }

      .bsf-input {
        width: 100%;
        min-height: 46px;
        border: 1.5px solid #DCE6F7;
        border-radius: 12px;
        padding: 10px 13px;
        font-size: 14.5px;
        font-family: inherit;
        color: #0A2354;
        background: #FBFCFF;
        outline: none;
        box-sizing: border-box;
        transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
      }

      .bsf-input:focus {
        border-color: #0EA5E9;
        background: #ffffff;
        box-shadow: 0 0 0 4px rgba(14,165,233,0.12);
      }

      .bsf-input[readonly] {
        cursor: not-allowed;
        color: #52627F;
        background: #F1F5FB;
        border-color: #DCE6F7;
      }

      .bsf-input[readonly]:focus {
        box-shadow: none;
        background: #F1F5FB;
        border-color: #DCE6F7;
      }

      .bsf-account-notice {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        margin: -4px 0 18px;
        padding: 12px 14px;
        border: 1px solid #C7DBFB;
        border-radius: 14px;
        background: #EEF4FE;
        color: #0A2354;
      }

      .bsf-account-notice-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        border-radius: 50%;
        background: #0EA5E9;
        color: #ffffff;
        font-size: 12px;
        font-weight: 800;
      }

      .bsf-account-notice strong {
        display: block;
        font-size: 13px;
      }

      .bsf-account-notice p {
        margin: 3px 0 0;
        color: #5F7194;
        font-size: 12px;
        line-height: 1.45;
      }

      .bsf-textarea { resize: vertical; min-height: 110px; }

      .bsf-select { cursor: pointer; }

      /* ---------- Question fields ---------- */
      .bsf-fields-list { display: flex; flex-direction: column; gap: 14px; }

      .bsf-field-card {
        border: 1.5px solid #E3EBF9;
        background: #FAFCFF;
        border-radius: 18px;
        padding: 18px;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
      }

      .bsf-field-card--active {
        border-color: #0EA5E9;
        box-shadow: 0 6px 18px rgba(14,165,233,0.1);
        background: #ffffff;
      }

      .bsf-field-label {
        display: flex;
        align-items: baseline;
        gap: 9px;
        margin-bottom: 0;
      }

      .bsf-field-number {
        font-family: 'Fraunces', Georgia, serif;
        font-weight: 600;
        font-size: 13px;
        color: #0EA5E9;
        flex-shrink: 0;
      }

      .bsf-field-label-text { flex: 1; }

      .bsf-field-check {
        color: #0EA5E9;
        font-size: 13px;
        font-weight: 800;
      }

      .bsf-field-control { margin-top: 12px; }

      .bsf-option-list { display: flex; flex-wrap: wrap; gap: 9px; }

      .bsf-option-item {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        border: 1.5px solid #DCE6F7;
        background: #ffffff;
        border-radius: 12px;
        padding: 8px 12px;
        cursor: pointer;
        font-size: 13.5px;
        font-weight: 500;
        color: #2C3E63;
        transition: border-color 0.15s ease, background 0.15s ease;
      }

      .bsf-option-item--active {
        border-color: #0EA5E9;
        background: #EAF1FE;
        color: #0A2354;
        font-weight: 600;
      }

      .bsf-rating-wrap { display: flex; flex-wrap: wrap; gap: 9px; }

      .bsf-rating-btn {
        width: 42px; height: 42px;
        border: 1.5px solid #DCE6F7;
        border-radius: 12px;
        background: #ffffff;
        color: #2C3E63;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .bsf-rating-btn--active {
        background: #0EA5E9;
        color: #ffffff;
        border-color: #0EA5E9;
      }

      .bsf-file-drop {
        display: flex;
        align-items: center;
        gap: 10px;
        border: 1.5px dashed #C4D5F5;
        border-radius: 14px;
        padding: 14px;
        background: #FBFCFF;
        cursor: pointer;
        transition: border-color 0.15s ease, background 0.15s ease;
      }

      .bsf-file-drop:hover { border-color: #0EA5E9; background: #EEF4FE; }
      .bsf-file-drop input[type="file"] { display: none; }
      .bsf-file-drop-icon { font-size: 18px; }
      .bsf-file-drop-text { font-size: 13.5px; color: #3D5480; font-weight: 500; }

      .bsf-helper-text { margin: 8px 0 0; font-size: 12px; color: #8093B5; }

      /* ---------- Actions ---------- */
      .bsf-action-row {
        display: flex;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 12px;
        border-top: 1px solid #EAF1FE;
        margin-top: 28px;
        padding-top: 22px;
      }

      .bsf-btn {
        min-height: 46px;
        border-radius: 13px;
        padding: 12px 22px;
        font-size: 14px;
        font-weight: 700;
        cursor: pointer;
        font-family: inherit;
        display: inline-flex;
        align-items: center;
        gap: 9px;
        transition: transform 0.1s ease, box-shadow 0.15s ease, opacity 0.15s ease;
      }

      .bsf-btn:active:not(:disabled) { transform: translateY(1px); }

      .bsf-btn-primary {
        border: none;
        background: #0EA5E9;
        color: #ffffff;
        box-shadow: 0 10px 24px rgba(14,165,233,0.28);
      }

      .bsf-btn-secondary {
        border: 1.5px solid #DCE6F7;
        background: #ffffff;
        color: #2C3E63;
      }

      .bsf-btn:disabled { opacity: 0.6; cursor: not-allowed; }

      .bsf-spinner {
        width: 14px; height: 14px;
        border: 2px solid rgba(255,255,255,0.4);
        border-top-color: #ffffff;
        border-radius: 50%;
        display: inline-block;
        animation: bsf-spin 0.7s linear infinite;
      }

      @keyframes bsf-spin { to { transform: rotate(360deg); } }

      .bsf-footer {
        padding: 26px 8px 8px;
        text-align: center;
        color: #8093B5;
        font-size: 12px;
        font-weight: 500;
      }

      /* ---------- Empty / error state ---------- */
      .bsf-state-card {
        max-width: 480px;
        margin: 100px auto;
        background: #ffffff;
        border: 1px solid #E3EBF9;
        border-radius: 24px;
        padding: 36px 32px;
        text-align: center;
        box-shadow: 0 10px 30px rgba(10, 35, 84, 0.06);
      }

      .bsf-state-icon {
        width: 52px; height: 52px;
        margin: 0 auto 16px;
        border-radius: 50%;
        background: #EAF1FE;
        color: #0EA5E9;
        display: flex; align-items: center; justify-content: center;
        font-size: 22px;
      }

      .bsf-state-title {
        margin: 0 0 10px;
        font-family: 'Fraunces', Georgia, serif;
        font-size: 22px;
        color: #0A2354;
        font-weight: 600;
      }

      .bsf-state-text { color: #6B7A9B; line-height: 1.6; font-size: 14px; }

      /* ---------- Skeleton loading ---------- */
      .bsf-skel-hero {
        background: #0EA5E9;
        border-radius: 22px;
        padding: 26px 28px;
      }
      .bsf-skel-row { display: flex; align-items: center; justify-content: space-between; }
      .bsf-skel-card {
        margin-top: 22px;
        background: #ffffff;
        border: 1px solid #E3EBF9;
        border-radius: 22px;
        padding: 24px;
      }
      .bsf-skel-block {
        border-radius: 8px;
        background: linear-gradient(90deg, rgba(255,255,255,0.14) 25%, rgba(255,255,255,0.28) 37%, rgba(255,255,255,0.14) 63%);
        background-size: 400% 100%;
        animation: bsf-shimmer 1.4s ease infinite;
      }
      .bsf-skel-card .bsf-skel-block {
        background: linear-gradient(90deg, #EAF1FE 25%, #F5F8FD 37%, #EAF1FE 63%);
        background-size: 400% 100%;
      }
      @keyframes bsf-shimmer {
        0% { background-position: 100% 50%; }
        100% { background-position: 0 50%; }
      }

      @media (prefers-reduced-motion: reduce) {
        .bsf-spinner, .bsf-skel-block { animation: none; }
      }

      @media (max-width: 560px) {
        .bsf-form-card { padding: 20px; border-radius: 18px; }
        .bsf-hero { padding: 22px 20px 20px; }
        .bsf-action-row { justify-content: stretch; }
        .bsf-action-row .bsf-btn { flex: 1; justify-content: center; }
      }
    `}</style>
  );
}
